import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import { Database, Code2, Sparkles, Eye, EyeOff, Mail, Smartphone, Info, Palette, Check } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { useTheme } from "../context/ThemeContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/v2`;

const AuthPage = ({ onLogin }) => {
  const { theme, setTheme, activeThemeObj, themes } = useTheme();
  const [loginForm, setLoginForm] = useState({ identifier: "", password: "" }); // 'identifier' can be email or phone
  const [registerForm, setRegisterForm] = useState({ email: "", phone_number: "", password: "", full_name: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0); // 0-4 for strength
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [registerMethod, setRegisterMethod] = useState("email"); // "email" or "phone"
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerificationLoading, setOtpVerificationLoading] = useState(false);
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [isRegistering, setIsRegistering] = useState(false); // To differentiate initial registration from OTP flow
  const [featureFlags, setFeatureFlags] = useState({
    FEATURE_EMAIL_REGISTRATION: false,
    FEATURE_PHONE_REGISTRATION: false,
  });

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        const response = await axios.get(`${API}/feature-flags/`);
        setFeatureFlags(response.data);
        // Set default registration method based on available flags
        if (response.data.FEATURE_EMAIL_REGISTRATION) {
          setRegisterMethod("email");
        } else if (response.data.FEATURE_PHONE_REGISTRATION) {
          setRegisterMethod("phone");
        }
      } catch (error) {
        console.error("Failed to fetch feature flags:", error);
        // Default to email if fetching fails
        setFeatureFlags({
          FEATURE_EMAIL_REGISTRATION: true,
          FEATURE_PHONE_REGISTRATION: false,
        });
        setRegisterMethod("email");
      }
    };
    fetchFeatureFlags();
  }, []);

  // Function to check password strength
  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length > 7) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/\d/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    return strength;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = loginForm.identifier.includes('@')
        ? { email: loginForm.identifier, password: loginForm.password }
        : { phone_number: loginForm.identifier, password: loginForm.password };

      const response = await axios.post(`${API}/auth/login/`, payload);
      const token = response.data.access_token;

      // Get user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // toast.success("Welcome back!");
      onLogin(token, userResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleFillDemo = () => {
    setLoginForm({
      identifier: "data.pilot@demo.demo",
      password: "Demo@123!"
    });
    toast.info("Demo credentials filled!");
  };


  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsRegistering(true); // Indicate that initial registration is in progress

    try {
      const payload = registerMethod === "email"
        ? { ...registerForm, phone_number: undefined }
        : { ...registerForm, email: undefined };

      await axios.post(`${API}/auth/register/`, payload);

      if (registerMethod === "email") {
        toast.success("Account created successfully! Please check your email to verify your account.");
      } else {
        toast.success("Account created successfully! An OTP has been sent to your phone number.");
        setOtpSent(true);
        setOtpResendTimer(60); // Start 60-second timer for OTP resend
        const timer = setInterval(() => {
          setOtpResendTimer((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp/`, { phone_number: registerForm.phone_number });
      toast.success("New OTP sent successfully!");
      setOtpSent(true);
      setOtpResendTimer(60); // Reset timer
      const timer = setInterval(() => {
        setOtpResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpVerificationLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp/`, { phone_number: registerForm.phone_number, otp: otp });
      toast.success("Phone number verified successfully! You can now log in.");
      setOtpSent(false); // Reset OTP state
      setIsRegistering(false); // Reset registration state
      setOtp(""); // Clear OTP input
      // Optionally, switch to login tab or navigate
    } catch (error) {
      toast.error(error.response?.data?.detail || "OTP verification failed.");
    } finally {
      setOtpVerificationLoading(false);
    }
  };

  return (
    <div className={`relative min-h-screen flex items-center justify-center p-3 sm:p-6 lg:p-8 transition-colors duration-300 ${activeThemeObj.styles?.appBg || 'bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 text-slate-900'}`}>
      {/* Top right Theme Switcher */}
      <div className="absolute top-4 right-4 z-50">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              data-testid="auth-theme-selector"
              className={`gap-2 backdrop-blur-md transition-colors ${
                activeThemeObj.isDark
                  ? 'bg-slate-900/80 border-slate-700 text-slate-200 hover:bg-slate-800'
                  : 'bg-white/80 border-slate-200 text-slate-800 hover:bg-slate-100'
              }`}
            >
              <Palette className="h-4 w-4 text-blue-500" />
              <span className="text-xs font-medium">{activeThemeObj.name}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 p-1.5">
            <div className="px-2 py-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              Select Theme
            </div>
            {themes.map((t) => (
              <DropdownMenuItem
                key={t.id}
                onClick={() => setTheme(t.id)}
                data-testid={`auth-theme-option-${t.id}`}
                className="flex items-center justify-between cursor-pointer text-xs py-2"
              >
                <div className="flex items-center gap-2">
                  <span className={`h-3 w-3 rounded-full ${t.preview.accent}`} />
                  <span className="font-medium">{t.name}</span>
                  {t.isDark ? (
                    <span className="text-[9px] px-1 rounded bg-slate-800 text-slate-400">Dark</span>
                  ) : (
                    <span className="text-[9px] px-1 rounded bg-slate-200 text-slate-600">Light</span>
                  )}
                </div>
                {theme === t.id && <Check className="h-3.5 w-3.5 text-blue-500" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
        {/* Left side - Branding */}
        <div className="space-y-6 sm:space-y-8 text-left">
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center gap-3" data-testid="logo-section">
              <div className="h-11 w-11 sm:h-14 sm:w-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
                <Database className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
              <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${activeThemeObj.isDark ? 'text-slate-100' : 'bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent'}`}>
                DataPilot v2
              </h1>
            </div>
            <p className={`text-sm sm:text-base lg:text-lg max-w-md ${activeThemeObj.isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Your personal SQL learning environment. Practice queries safely with isolated databases.
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <div className={`flex items-start gap-3 sm:gap-4 p-3 sm:p-4 backdrop-blur-sm rounded-xl border transition-colors ${
              activeThemeObj.isDark
                ? 'bg-slate-900/60 border-slate-800/80 text-slate-200 shadow-md'
                : 'bg-white/60 border-slate-200 text-slate-900'
            }`}>
              <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Database className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
              </div>
              <div>
                <h3 className={`font-semibold text-sm sm:text-base mb-0.5 sm:mb-1 ${activeThemeObj.isDark ? 'text-slate-100' : 'text-slate-900'}`}>Isolated Database</h3>
                <p className={`text-xs sm:text-sm ${activeThemeObj.isDark ? 'text-slate-400' : 'text-slate-600'}`}>Your own SQLite database with sample data to practice</p>
              </div>
            </div>

            <div className={`flex items-start gap-3 sm:gap-4 p-3 sm:p-4 backdrop-blur-sm rounded-xl border transition-colors ${
              activeThemeObj.isDark
                ? 'bg-slate-900/60 border-slate-800/80 text-slate-200 shadow-md'
                : 'bg-white/60 border-slate-200 text-slate-900'
            }`}>
              <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                <Code2 className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-500" />
              </div>
              <div>
                <h3 className={`font-semibold text-sm sm:text-base mb-0.5 sm:mb-1 ${activeThemeObj.isDark ? 'text-slate-100' : 'text-slate-900'}`}>Smart SQL Editor</h3>
                <p className={`text-xs sm:text-sm ${activeThemeObj.isDark ? 'text-slate-400' : 'text-slate-600'}`}>Syntax highlighting and query history tracking</p>
              </div>
            </div>

            <div className={`flex items-start gap-3 sm:gap-4 p-3 sm:p-4 backdrop-blur-sm rounded-xl border transition-colors ${
              activeThemeObj.isDark
                ? 'bg-slate-900/60 border-slate-800/80 text-slate-200 shadow-md'
                : 'bg-white/60 border-slate-200 text-slate-900'
            }`}>
              <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500" />
              </div>
              <div>
                <h3 className={`font-semibold text-sm sm:text-base mb-0.5 sm:mb-1 ${activeThemeObj.isDark ? 'text-slate-100' : 'text-slate-900'}`}>Safe Learning</h3>
                <p className={`text-xs sm:text-sm ${activeThemeObj.isDark ? 'text-slate-400' : 'text-slate-600'}`}>Query timeouts and easy database reset</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Auth form */}
        <Card
          className={`border shadow-xl w-full max-w-md mx-auto lg:max-w-none transition-colors ${
            activeThemeObj.styles?.cardBg || (activeThemeObj.isDark ? 'bg-slate-900/90 border-slate-800 text-slate-100' : 'bg-white border-slate-200 text-slate-900')
          }`}
          data-testid="auth-card"
        >
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className={`text-xl sm:text-2xl ${activeThemeObj.isDark ? 'text-slate-100' : ''}`}>Get Started</CardTitle>
            <CardDescription className={`text-xs sm:text-sm ${activeThemeObj.isDark ? 'text-slate-400' : ''}`}>Login or create an account to start learning SQL</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login" data-testid="login-form">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-identifier" className={activeThemeObj.isDark ? 'text-slate-200' : ''}>Email or Phone</Label>
                    <Input
                      id="login-identifier"
                      data-testid="login-identifier-input"
                      type="text"
                      placeholder="your.email@example.com or +1234567890"
                      value={loginForm.identifier}
                      onChange={(e) => setLoginForm({ ...loginForm, identifier: e.target.value })}
                      className={activeThemeObj.isDark ? 'bg-slate-950/80 border-slate-800 text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500' : ''}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className={activeThemeObj.isDark ? 'text-slate-200' : ''}>Password</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className={activeThemeObj.isDark ? 'bg-slate-950/80 border-slate-800 text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500' : ''}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className={`w-full ${activeThemeObj.styles?.accentColor || ''}`}
                    disabled={loading}
                    data-testid="login-submit-button"
                  >
                    {loading ? "Logging in..." : "Login"}
                  </Button>

                  <Alert className={`mt-6 transition-colors ${
                    activeThemeObj.isDark
                      ? 'bg-blue-950/40 border-blue-900/60 text-blue-200'
                      : 'bg-blue-50 border-blue-200 text-blue-900'
                  }`}>
                    <Info className={`h-4 w-4 ${activeThemeObj.isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <AlertTitle className={`font-semibold ${activeThemeObj.isDark ? 'text-blue-300' : 'text-blue-800'}`}>Demo Credentials</AlertTitle>
                    <AlertDescription className={activeThemeObj.isDark ? 'text-blue-300/90' : 'text-blue-700'}>
                      <div className="flex flex-col space-y-1 mt-2">
                        <div className="flex justify-between items-center flex-wrap gap-1">
                          <span>Email: <code className={`px-1 rounded font-mono text-xs break-all ${
                            activeThemeObj.isDark ? 'bg-blue-900/60 text-blue-200 border border-blue-800/60' : 'bg-blue-100 text-blue-900'
                          }`}>data.pilot@demo.demo</code></span>
                        </div>
                        <div className="flex justify-between items-center flex-wrap gap-1">
                          <span>Pass: <code className={`px-1 rounded font-mono text-xs ${
                            activeThemeObj.isDark ? 'bg-blue-900/60 text-blue-200 border border-blue-800/60' : 'bg-blue-100 text-blue-900'
                          }`}>Demo@123!</code></span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleFillDemo}
                          className={`mt-2 h-8 text-xs transition-colors ${
                            activeThemeObj.isDark
                              ? 'border-blue-700/60 text-blue-300 hover:bg-blue-900/60 hover:text-blue-200 bg-slate-900/60'
                              : 'border-blue-300 text-blue-700 hover:bg-blue-100 hover:text-blue-800 bg-white/50'
                          }`}
                        >
                          Auto-fill Demo Account
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                </form>
              </TabsContent>

              <TabsContent value="register" data-testid="register-form">
                {!otpSent ? (
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className={activeThemeObj.isDark ? 'text-slate-200' : ''}>Full Name</Label>
                      <Input
                        id="register-name"
                        data-testid="register-name-input"
                        type="text"
                        placeholder="John Doe"
                        value={registerForm.full_name}
                        onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                        className={activeThemeObj.isDark ? 'bg-slate-950/80 border-slate-800 text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500' : ''}
                        required
                      />
                    </div>

                    {!featureFlags.FEATURE_EMAIL_REGISTRATION && featureFlags.FEATURE_PHONE_REGISTRATION && (
                      <Button
                        type="button"
                        variant="default"
                        className="w-full"
                        disabled
                      >
                        <Smartphone className="h-4 w-4 mr-2" /> Register with Phone
                      </Button>
                    )}

                    {featureFlags.FEATURE_EMAIL_REGISTRATION && featureFlags.FEATURE_PHONE_REGISTRATION && (
                      <div className="flex flex-col sm:flex-row items-center gap-2">
                        <Button
                          type="button"
                          variant={registerMethod === "email" ? "default" : "outline"}
                          onClick={() => setRegisterMethod("email")}
                          className="w-full sm:flex-1 text-xs sm:text-sm"
                        >
                          <Mail className="h-4 w-4 mr-2" /> Register with Email
                        </Button>
                        <Button
                          type="button"
                          variant={registerMethod === "phone" ? "default" : "outline"}
                          onClick={() => setRegisterMethod("phone")}
                          className="w-full sm:flex-1 text-xs sm:text-sm"
                        >
                          <Smartphone className="h-4 w-4 mr-2" /> Register with Phone
                        </Button>
                      </div>
                    )}

                    {!featureFlags.FEATURE_EMAIL_REGISTRATION && !featureFlags.FEATURE_PHONE_REGISTRATION && (
                      <p className="text-center text-red-500 text-sm">Registration is currently disabled.</p>
                    )}

                    {registerMethod === "email" && featureFlags.FEATURE_EMAIL_REGISTRATION && (
                      <div className="space-y-2">
                        <Label htmlFor="register-email" className={activeThemeObj.isDark ? 'text-slate-200' : ''}>Email</Label>
                        <Input
                          id="register-email"
                          data-testid="register-email-input"
                          type="email"
                          placeholder="your.email@example.com"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                          className={activeThemeObj.isDark ? 'bg-slate-950/80 border-slate-800 text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500' : ''}
                          required={registerMethod === "email"}
                        />
                      </div>
                    )}

                    {registerMethod === "phone" && featureFlags.FEATURE_PHONE_REGISTRATION && (
                      <div className="space-y-2">
                        <Label htmlFor="register-phone" className={activeThemeObj.isDark ? 'text-slate-200' : ''}>Phone Number</Label>
                        <Input
                          id="register-phone"
                          data-testid="register-phone-input"
                          type="tel"
                          placeholder="+1234567890"
                          value={registerForm.phone_number}
                          onChange={(e) => setRegisterForm({ ...registerForm, phone_number: e.target.value })}
                          className={activeThemeObj.isDark ? 'bg-slate-950/80 border-slate-800 text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500' : ''}
                          required={registerMethod === "phone"}
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="register-password" className={activeThemeObj.isDark ? 'text-slate-200' : ''}>Password</Label>
                      <div className="relative">
                        <Input
                          id="register-password"
                          data-testid="register-password-input"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={registerForm.password}
                          onChange={(e) => {
                            const newPassword = e.target.value;
                            setRegisterForm((prev) => ({ ...prev, password: newPassword }));
                            setPasswordStrength(checkPasswordStrength(newPassword));
                            setPasswordsMatch(newPassword === registerForm.confirm_password);
                          }}
                          className={activeThemeObj.isDark ? 'bg-slate-950/80 border-slate-800 text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500' : ''}
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="sr-only">{showPassword ? "Hide password" : "Show password"}</span>
                        </Button>
                      </div>
                      {/* Password strength indicator */}
                      <div className={`h-2 w-full rounded-full mt-1 ${activeThemeObj.isDark ? 'bg-slate-800' : 'bg-gray-200'}`}>
                        <div
                          className={`h-full rounded-full transition-all duration-300
                            ${passwordStrength === 0 && "bg-transparent"}
                            ${passwordStrength === 1 && "bg-red-500 w-1/4"}
                            ${passwordStrength === 2 && "bg-orange-500 w-2/4"}
                            ${passwordStrength === 3 && "bg-yellow-500 w-3/4"}
                            ${passwordStrength === 4 && "bg-green-500 w-full"}
                          `}
                        ></div>
                      </div>
                      {passwordStrength > 0 && (
                        <p className={`text-xs sm:text-sm mt-1 ${passwordStrength < 3 ? "text-red-500" : "text-green-500"}`}>
                          {passwordStrength === 1 && "Very Weak"}
                          {passwordStrength === 2 && "Weak"}
                          {passwordStrength === 3 && "Moderate"}
                          {passwordStrength === 4 && "Strong"}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-confirm-password" className={activeThemeObj.isDark ? 'text-slate-200' : ''}>Confirm Password</Label>
                      <div className="relative">
                        <Input
                          id="register-confirm-password"
                          data-testid="register-confirm-password-input"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={registerForm.confirm_password}
                          onChange={(e) => {
                            const newConfirmPassword = e.target.value;
                            setRegisterForm((prev) => ({ ...prev, confirm_password: newConfirmPassword }));
                            setPasswordsMatch(registerForm.password === newConfirmPassword);
                          }}
                          className={activeThemeObj.isDark ? 'bg-slate-950/80 border-slate-800 text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500' : ''}
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="sr-only">{showConfirmPassword ? "Hide password" : "Show password"}</span>
                        </Button>
                      </div>
                      {/* Confirm password match indicator */}
                      {registerForm.confirm_password.length > 0 && (
                        <p className={`text-xs sm:text-sm mt-1 ${passwordsMatch ? "text-green-500" : "text-red-500"}`}>
                          {passwordsMatch ? "Passwords match" : "Passwords do not match"}
                        </p>
                      )}
                    </div>
                    <Button
                      type="submit"
                      className={`w-full ${activeThemeObj.styles?.accentColor || ''}`}
                      disabled={loading || !passwordsMatch || passwordStrength < 3 || (registerMethod === "email" && !registerForm.email && featureFlags.FEATURE_EMAIL_REGISTRATION) || (registerMethod === "phone" && !registerForm.phone_number && featureFlags.FEATURE_PHONE_REGISTRATION)}
                      data-testid="register-submit-button"
                    >
                      {loading ? "Creating account..." : "Create Account"}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOtp} className="space-y-4">
                    <div className="space-y-2 text-center">
                      <p className={`text-xs sm:text-sm ${activeThemeObj.isDark ? 'text-slate-400' : 'text-slate-600'}`}>An OTP has been sent to {registerForm.phone_number}. Please enter it below to verify your account.</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="otp" className={activeThemeObj.isDark ? 'text-slate-200' : ''}>Verification Code</Label>
                      <Input
                        id="otp"
                        data-testid="otp-input"
                        type="text"
                        placeholder="Enter OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        className={activeThemeObj.isDark ? 'bg-slate-950/80 border-slate-800 text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500' : ''}
                        required
                        maxLength={6}
                      />
                    </div>
                    <Button type="submit" className={`w-full ${activeThemeObj.styles?.accentColor || ''}`} disabled={otpVerificationLoading || otp.length !== 6}>
                      {otpVerificationLoading ? "Verifying..." : "Verify OTP"}
                    </Button>
                    <Button
                      type="button"
                      variant="link"
                      className={`w-full ${activeThemeObj.isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                      onClick={handleSendOtp}
                      disabled={loading || otpResendTimer > 0}
                    >
                      {otpResendTimer > 0 ? `Resend OTP in ${otpResendTimer}s` : "Resend OTP"}
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
